import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "@phosphor-icons/react";
import { HERO_BG, HERO_VIDEO } from "@/data/content";

const LINES = ["Trasformiamo", "superfici in", "identità."];

const lineWrap = { hidden: {}, show: { transition: { staggerChildren: 0.1, delayChildren: 1.1 } } };
const lineItem = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  return (
    <section id="top" ref={ref} className="relative h-[100svh] w-full overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <video
          src={HERO_VIDEO}
          poster={HERO_BG}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          data-testid="hero-video"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-brand-dark" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/20 to-brand-dark/40" />

      {/* intro curtain */}
      <motion.div
        initial={{ scaleY: 1 }}
        animate={{ scaleY: 0 }}
        transition={{ duration: 0.9, delay: 0.85, ease: [0.87, 0, 0.13, 1] }}
        style={{ transformOrigin: "top" }}
        className="absolute inset-0 z-30 bg-brand-red"
      />
      <motion.div
        initial={{ scaleY: 1 }}
        animate={{ scaleY: 0 }}
        transition={{ duration: 0.9, delay: 0.65, ease: [0.87, 0, 0.13, 1] }}
        style={{ transformOrigin: "top" }}
        className="absolute inset-0 z-20 bg-brand-dark"
      />

      <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 lg:px-12 flex flex-col justify-end pb-16 lg:pb-24">
        <motion.p
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="font-outfit text-xs sm:text-sm tracking-[0.35em] uppercase text-brand-red font-bold mb-6 flex items-center gap-3"
          data-testid="hero-overline"
        >
          <span className="h-px w-10 bg-brand-red" /> Car Wrapping & Visual Communication
        </motion.p>

        <motion.h1
          variants={lineWrap}
          initial="hidden"
          animate="show"
          className="font-anton uppercase leading-[0.86] text-[15vw] md:text-[13vw] lg:text-[10.5vw] tracking-tight"
        >
          {LINES.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span variants={lineItem} className="block">
                {i === 2 ? (
                  <>
                    <span className="text-stroke-red">identità</span>
                    <span className="text-brand-red">.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 0.9 }}
          className="mt-8 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="max-w-md font-manrope font-light text-neutral-300 text-base lg:text-lg leading-relaxed">
            New Graphic — lo studio di car wrapping e comunicazione visiva a Teglio, Valtellina. Pellicole premium, mani esperte, risultati che parlano.
          </p>
          <div className="flex items-center gap-3 text-neutral-400 font-outfit text-xs uppercase tracking-widest">
            <ArrowDown size={18} className="animate-bounce text-brand-red" />
            Scorri per esplorare
          </div>
        </motion.div>
      </div>
    </section>
  );
};
